import { useMatch, useNavigate } from "react-router";
import FormTemplate from "../../templates/FormTemplate";
import { PrintService } from "../../../services/api";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { TicketPrintTemplate } from "../../../services/swagger";
import { useGlobalContext } from "../../../stores/Global";
import { Grid } from "@mui/material";
import FormInput from "../../molecules/FormInput";
import ImageInput from "../../molecules/ImageInput";
import FieldLabel from "../../atoms/FieldLabel";
import SectionTitle from "../../atoms/SectionTitle";
import { useMutation } from "@tanstack/react-query";

export default function TicketPrintTemplatePage() {
  const { context } = useGlobalContext();
  const { t } = useTranslation("TicketPrintTemplate");
  const navigate = useNavigate();
  const match = useMatch("/m/settings/ticket-print-template/:_id");
  const _id = match?.params._id === "create" ? undefined : match?.params._id;
  const [name, setName] = useState<string>("");

  const [banner, setBanner] = useState<string>("");
  const [priority, setPriority] = useState<boolean>(false);
  const [department, setDepartment] = useState<boolean>(false);
  const [idField, setIdField] = useState<boolean>(false);
  const [title, setTitle] = useState<boolean>(false);
  const [body, setBody] = useState<boolean>(false);

  const [contactCompany, setContactCompany] = useState<boolean>(false);
  const [contactPerson, setContactPerson] = useState<boolean>(false);
  const [contactPhone, setContactPhone] = useState<boolean>(false);
  const [contactLocation, setContactLocation] = useState<boolean>(false);

  const [assignees, setAssignees] = useState<boolean>(false);

  const [taskTitle, setTaskTitle] = useState<boolean>(false);
  const [taskDescription, setTaskDescription] = useState<boolean>(false);
  const [taskUsers, setTaskUsers] = useState<boolean>(false);
  const [taskComment, setTaskComment] = useState<boolean>(false);
  const [taskTimes, setTaskTimes] = useState<boolean>(false);
  const [taskItems, setTaskItems] = useState<boolean>(false);
  const [taskType, setTaskType] = useState<boolean>(false);

  const [responseContent, setResponseContent] = useState<boolean>(false);
  const [attachments, setAttachments] = useState<boolean>(false);
  const [privateResponse, setPrivateResponse] = useState<boolean>(false);

  const [maxResponses, setMaxResponses] = useState<number>(0);
  const [maxTasks, setMaxTasks] = useState<number>(0);
  const [backgroundColor, setBackgroundColor] = useState<string>("#ffffff");
  const [textColor, setTextColor] = useState<string>("#000000");
  const [sectionTitleColor, setSectionTitleColor] = useState<string>("#42A5F5");
  const [forwardEmails, setForwardEmails] = useState<string[]>([]);
  const [copyTemplate, setCopyTemplate] = useState<string>("");
  const [signatureRequired, setSignatureRequired] = useState<boolean>(false);
  const [displayTicketUser, setDisplayTicketUser] = useState<boolean>(false);
  const [displayResponder, setDisplayResponder] = useState<boolean>(false);

  const [templates, setTemplates] = useState<TicketPrintTemplate[]>([]);

  const {
    mutate: loadTicketPrintTemplateList,
    isPending: isTemplateListLoading,
  } = useMutation({
    mutationFn: async () => {
      const res = await PrintService.getTicketPrintTemplateList();
      return res?.data?.data;
    },
    onSuccess: (data) => {
      if (data) setTemplates(data);
    },
  });

  const { mutate: loadTicketPrintTemplate, isPending: isPrintTemplateLoading } =
    useMutation({
      mutationFn: async () => {
        if (!_id) {
          setIdField(true);
          setTitle(true);
          setBody(true);
          return;
        }
        const res = await PrintService.getTicketPrintTemplate({ id: _id });
        return res?.data?.data;
      },
      onSuccess: (data) => {
        if (!data) return;
        setName(data.name);
        setIdField(data.fields.id);
        setTitle(data.fields.title);
        setBody(data.fields.body);
        setBanner(data.fields.banner);
        setPriority(data.fields.priority);
        setDepartment(data.fields.department);
        setContactCompany(data.fields.contactInfo.company);
        setContactPerson(data.fields.contactInfo.person);
        setContactPhone(data.fields.contactInfo.phone);
        setContactLocation(data.fields.contactInfo.location);
        setAssignees(data.fields.assignees);
        setTaskTitle(data.fields.tasks.title);
        setTaskDescription(data.fields.tasks.description);
        setTaskUsers(data.fields.tasks.users);
        setTaskComment(data.fields.tasks.comment);
        setTaskTimes(data.fields.tasks.times);
        setTaskItems(data.fields.tasks.items);
        setTaskType(data.fields.tasks.taskType);
        setResponseContent(data.fields.responses.content);
        setAttachments(data.fields.responses.attachments ?? false);
        setPrivateResponse(data.fields.responses.privateResponse ?? false);
        setMaxResponses(data.options.maxResponses);
        setMaxTasks(data.options.maxTasks);
        setBackgroundColor(data.options.backgroundColor);
        setTextColor(data.options.textColor);
        setSectionTitleColor(data.options.sectionTitleColor);
        setForwardEmails(data.options.forwardEmails);
        setCopyTemplate(data.options.copyTemplate);
        setSignatureRequired(data.options.signatureRequired);
        setDisplayTicketUser(data.options.displayTicketUser);
        setDisplayResponder(data.options.displayResponder);
      },
    });

  const handleClose = () => {
    navigate(-1);
  };

  const handleSubmit = async () => {
    const ticketPrintTemplate: TicketPrintTemplate = {
      fields: {
        assignees,
        banner,
        contactInfo: {
          company: contactCompany,
          location: contactLocation,
          person: contactPerson,
          phone: contactPhone,
        },
        department,
        id: idField,
        title,
        body,
        priority,
        responses: {
          content: responseContent,
          attachments,
          privateResponse,
        },
        serviceCall: {
          companyName: false,
          contactInfo: false,
          contactName: false,
          id: false,
        },
        tasks: {
          comment: taskComment,
          description: taskDescription,
          items: taskItems,
          times: taskTimes,
          title: taskTitle,
          users: taskUsers,
          taskType: taskType,
        },
      },
      name,
      options: {
        backgroundColor,
        copyTemplate,
        maxResponses,
        maxTasks,
        sectionTitleColor,
        signatureRequired,
        forwardEmails,
        textColor,
        displayResponder,
        displayTicketUser,
      },
    };
    if (!_id) {
      const res = await PrintService.createTicketPrintTemplate({
        ticketPrintTemplate,
      });
      if (res?.data?.success) handleClose();
      return res;
    } else {
      return await PrintService.updateTicketPrintTemplate({
        id: _id,
        ticketPrintTemplate,
      });
    }
  };

  useEffect(() => {
    if (!context.user) return;
    loadTicketPrintTemplate();
    loadTicketPrintTemplateList();
  }, [context]);

  const loading = !context.user || isPrintTemplateLoading;

  return (
    <FormTemplate
      title={t("TicketPrintTemplate")}
      disableCancelButton
      submitButtonText={_id ? t("Save") : t("CreateTemplate")}
      disableEnter
      onSubmit={handleSubmit}
      onCancel={handleClose}
      noForm
      confirm
      loading={loading}
    >
      <Grid container rowGap={3} alignItems={"center"} sx={{ height: 1 }}>
        <Grid size={{ xs: 5, md: 3 }}>
          <FieldLabel children={t("TemplateName")} required />
        </Grid>
        <Grid size={{ xs: 7, md: 9 }}>
          <FormInput
            loading={loading}
            label={""}
            type="string"
            field={"name"}
            value={name}
            onChange={(e) => setName(e as string)}
            required
          />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <ImageInput
            label={t("Banner")}
            field={"banner"}
            value={banner}
            width="100%"
            onChange={(value) => setBanner(value)}
            loading={loading}
          />
        </Grid>

        {/* Contact Info */}
        <Grid size={{ xs: 12 }}>
          <SectionTitle subtitle={t("Fields.Subtitle")}>
            {t("Fields.Title")}
          </SectionTitle>
        </Grid>
        {/* Banner */}
        <Grid container size={{ xs: 12, md: 9 }} rowGap={3}>
          <Grid container size={{ xs: 12, md: 12 }} rowGap={1}>
            <Grid size={{ xs: 12, md: 4 }}>
              <FieldLabel>{t("TicketInfo")}</FieldLabel>
            </Grid>

            <Grid container size={{ xs: 12, md: 8 }} rowGap={1}>
              <Grid size={{ xs: 6, md: 6 }}>
                <FormInput
                  loading={loading}
                  label={t("ID")}
                  type="switch"
                  field={"idField"}
                  value={idField}
                  onChange={(e) => setIdField(e as boolean)}
                />
              </Grid>
              <Grid size={{ xs: 6, md: 6 }}>
                <FormInput
                  loading={loading}
                  label={t("Title")}
                  type="switch"
                  field={"title"}
                  value={title}
                  onChange={(e) => setTitle(e as boolean)}
                />
              </Grid>
              <Grid size={{ xs: 6, md: 6 }}>
                <FormInput
                  loading={loading}
                  label={t("Body")}
                  type="switch"
                  field={"body"}
                  value={body}
                  onChange={(e) => setBody(e as boolean)}
                />
              </Grid>
              <Grid size={{ xs: 6, md: 6 }}>
                <FormInput
                  loading={loading}
                  label={t("Priority")}
                  type="switch"
                  field={"priority"}
                  value={priority}
                  onChange={(e) => setPriority(e as boolean)}
                />
              </Grid>
              <Grid size={{ xs: 6, md: 6 }}>
                <FormInput
                  loading={loading}
                  label={t("Department")}
                  type="switch"
                  field={"department"}
                  value={department}
                  onChange={(e) => setDepartment(e as boolean)}
                />
              </Grid>
              <Grid size={{ xs: 6, md: 6 }}>
                <FormInput
                  loading={loading}
                  label={t("Responses")}
                  type="switch"
                  field={"responseContent"}
                  value={responseContent}
                  onChange={(e) => setResponseContent(e as boolean)}
                />
              </Grid>
              <Grid size={{ xs: 6, md: 6 }}>
                <FormInput
                  loading={loading}
                  label={"Attachments"}
                  type="switch"
                  field={"attachments"}
                  value={attachments}
                  onChange={(e) => setAttachments(e as boolean)}
                />
              </Grid>
              <Grid size={{ xs: 6, md: 6 }}>
                <FormInput
                  loading={loading}
                  label={"PrivateResponses"}
                  type="switch"
                  field={"privateResponse"}
                  value={privateResponse}
                  onChange={(e) => setPrivateResponse(e as boolean)}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid container size={{ xs: 12, md: 12 }} rowGap={1}>
            <Grid size={{ xs: 12, md: 4 }}>
              <FieldLabel>{t("ContactInfo")}</FieldLabel>
            </Grid>

            <Grid container size={{ xs: 12, md: 8 }} rowGap={1}>
              <Grid size={{ xs: 6, md: 6 }}>
                <FormInput
                  loading={loading}
                  label={t("Company")}
                  type="switch"
                  field={"contactCompany"}
                  value={contactCompany}
                  onChange={(e) => setContactCompany(e as boolean)}
                />
              </Grid>
              <Grid size={{ xs: 6, md: 6 }}>
                <FormInput
                  loading={loading}
                  label={t("Person")}
                  type="switch"
                  field={"contactPerson"}
                  value={contactPerson}
                  onChange={(e) => setContactPerson(e as boolean)}
                />
              </Grid>
              <Grid size={{ xs: 6, md: 6 }}>
                <FormInput
                  loading={loading}
                  label={t("Phone")}
                  type="switch"
                  field={"contactPhone"}
                  value={contactPhone}
                  onChange={(e) => setContactPhone(e as boolean)}
                />
              </Grid>
              <Grid size={{ xs: 6, md: 6 }}>
                <FormInput
                  loading={loading}
                  label={t("Location")}
                  type="switch"
                  field={"contactLocation"}
                  value={contactLocation}
                  onChange={(e) => setContactLocation(e as boolean)}
                />
              </Grid>
              <Grid size={{ xs: 6, md: 6 }}>
                <FormInput
                  loading={loading}
                  label={t("Assignees")}
                  type="switch"
                  field={"assignees"}
                  value={assignees}
                  onChange={(e) => setAssignees(e as boolean)}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid container size={{ xs: 12, md: 12 }} rowGap={1}>
            <Grid size={{ xs: 12, md: 4 }}>
              <FieldLabel>{t("Task")}</FieldLabel>
            </Grid>

            <Grid container size={{ xs: 12, md: 8 }} rowGap={1}>
              <Grid size={{ xs: 6, md: 6 }}>
                <FormInput
                  loading={loading}
                  label={t("Title")}
                  type="switch"
                  field={"taskTitle"}
                  value={taskTitle}
                  onChange={(e) => setTaskTitle(e as boolean)}
                />
              </Grid>
              <Grid size={{ xs: 6, md: 6 }}>
                <FormInput
                  loading={loading}
                  label={t("Description")}
                  type="switch"
                  field={"taskDescription"}
                  value={taskDescription}
                  onChange={(e) => setTaskDescription(e as boolean)}
                />
              </Grid>
              <Grid size={{ xs: 6, md: 6 }}>
                <FormInput
                  loading={loading}
                  label={t("TaskAssignees")}
                  type="switch"
                  field={"taskUsers"}
                  value={taskUsers}
                  onChange={(e) => setTaskUsers(e as boolean)}
                />
              </Grid>
              <Grid size={{ xs: 6, md: 6 }}>
                <FormInput
                  loading={loading}
                  label={t("Comments")}
                  type="switch"
                  field={"taskComment"}
                  value={taskComment}
                  onChange={(e) => setTaskComment(e as boolean)}
                />
              </Grid>
              <Grid size={{ xs: 6, md: 6 }}>
                <FormInput
                  loading={loading}
                  label={t("Timelogs")}
                  type="switch"
                  field={"taskTimes"}
                  value={taskTimes}
                  onChange={(e) => setTaskTimes(e as boolean)}
                />
              </Grid>
              <Grid size={{ xs: 6, md: 6 }}>
                <FormInput
                  loading={loading}
                  label={t("ItemsUsed")}
                  type="switch"
                  field={"taskItems"}
                  value={taskItems}
                  onChange={(e) => setTaskItems(e as boolean)}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        {/* Options */}
        <Grid size={{ xs: 12 }}>
          <SectionTitle subtitle={t("PrintTemplateControls.Subtitle")}>
            {t("PrintTemplateControls.Title")}
          </SectionTitle>
        </Grid>

        <Grid
          container
          size={{ xs: 12, md: 12 }}
          alignItems={"center"}
          rowGap={2}
        >
          <Grid size={{ xs: 5, md: 3 }}>
            <FieldLabel>{t("CopyTemplate")}</FieldLabel>
          </Grid>
          <Grid size={{ xs: 7, md: 9 }}>
            <FormInput
              loading={loading}
              label={""}
              type="list"
              list={templates.map((e) => ({
                name: e.name,
                value: e._id?.toString() || "",
              }))}
              field={"copyTemplate"}
              value={copyTemplate}
              onChange={(e) => setCopyTemplate(e as string)}
            />
          </Grid>
          <Grid size={{ xs: 5, md: 3 }}>
            <FieldLabel>{t("MaxResponses")}</FieldLabel>
          </Grid>
          <Grid size={{ xs: 7, md: 9 }}>
            <FormInput
              loading={loading}
              label={""}
              type="number"
              field={"maxResponses"}
              value={maxResponses}
              onChange={(e) => setMaxResponses(e as number)}
            />
          </Grid>
          <Grid size={{ xs: 5, md: 3 }}>
            <FieldLabel>{t("MaxTasks")}</FieldLabel>
          </Grid>
          <Grid size={{ xs: 7, md: 9 }}>
            <FormInput
              loading={loading}
              label={""}
              type="number"
              field={"maxTasks"}
              value={maxTasks}
              onChange={(e) => setMaxTasks(e as number)}
            />
          </Grid>

          <Grid size={{ xs: 5, md: 3 }}>
            <FieldLabel>{t("ForwardEmails")}</FieldLabel>
          </Grid>
          <Grid size={{ xs: 7, md: 9 }}>
            <FormInput
              loading={loading}
              label={""}
              type="chip-list"
              field={"copyTemplate"}
              value={forwardEmails}
              onChange={(e) => setForwardEmails(e as string[])}
            />
          </Grid>
          <Grid size={{ xs: 5, md: 3 }}>
            <FieldLabel>{t("SignatureRequired")}</FieldLabel>
          </Grid>
          <Grid size={{ xs: 7, md: 9 }}>
            <FormInput
              loading={loading}
              label={""}
              type="switch"
              field={"signatureRequired"}
              value={signatureRequired}
              onChange={(e) => setSignatureRequired(e as boolean)}
            />
          </Grid>
          <Grid size={{ xs: 5, md: 3 }}>
            <FieldLabel>{t("DisplayTicketUser")}</FieldLabel>
          </Grid>
          <Grid size={{ xs: 7, md: 9 }}>
            <FormInput
              loading={loading}
              label={""}
              type="switch"
              field={"displayTicketUser"}
              value={displayTicketUser}
              onChange={(e) => setDisplayTicketUser(e as boolean)}
            />
          </Grid>
          <Grid size={{ xs: 5, md: 3 }}>
            <FieldLabel>{t("DisplayResponder")}</FieldLabel>
          </Grid>
          <Grid size={{ xs: 7, md: 9 }}>
            <FormInput
              loading={loading}
              label={""}
              type="switch"
              field={"displayResponder"}
              value={displayResponder}
              onChange={(e) => setDisplayResponder(e as boolean)}
            />
          </Grid>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <SectionTitle subtitle={t("Themes.Subtitle")}>
            {t("Themes.Title")}
          </SectionTitle>
        </Grid>
        <Grid container rowGap={2} alignItems={"center"}>
          <Grid size={{ xs: 5, md: 4 }}>
            <FieldLabel children={t("BackgroundColor")} />
          </Grid>
          <Grid size={{ xs: 7, md: 8 }}>
            <FormInput
              loading={loading}
              label={""}
              type="color"
              field={"backgroundColor"}
              value={backgroundColor}
              onChange={(e) => setBackgroundColor(e as string)}
            />
          </Grid>
          <Grid size={{ xs: 5, md: 4 }}>
            <FieldLabel children={t("TextColor")} />
          </Grid>
          <Grid size={{ xs: 7, md: 8 }}>
            <FormInput
              loading={loading}
              label={""}
              type="color"
              field={"textColor"}
              value={textColor}
              onChange={(e) => setTextColor(e as string)}
            />
          </Grid>
          <Grid size={{ xs: 5, md: 4 }}>
            <FieldLabel children={t("SectionTitleColor")} />
          </Grid>
          <Grid size={{ xs: 7, md: 8 }}>
            <FormInput
              loading={loading}
              label={""}
              type="color"
              field={"sectionTitleColor"}
              value={sectionTitleColor}
              onChange={(e) => setSectionTitleColor(e as string)}
            />
          </Grid>
        </Grid>
      </Grid>
    </FormTemplate>
  );
}
